import {
    useEffect,
    useState
} from "react";

import {
    useParams
} from "react-router";

import {
    VocabularyCatalog
} from "../features/vocabulary/VocabularyCatalog.js";

import {
    parseVocabularyLevel
} from "../features/vocabulary/vocabularyLevels.js";

import { useContent } from "../services/content/ContentProvider.js";
import { loadPublishedVocabularyIndex } from "../services/content/publicContentReaders.js";

import {
    useI18n
} from "../i18n/I18nProvider.js";

import type {
    Level,
    VocabPackIndex
} from "../types/global.js";

import {
    BackButton
} from "../ui/components/Controls.js";

import {
    EmptyState,
    ErrorState,
    LoadingState
} from "../ui/components/Feedback.js";

import {
    Page,
    PageHeader
} from "../ui/components/Layout.js";

/**
 * Vocabulary pack catalog for one CEFR level.
 *
 * Responsibilities:
 * - validate the route level
 * - load the pack index
 * - expose loading/empty states
 * - delegate pack rendering to VocabularyCatalog
 */
function VocabularyLevelPage() {
    const { repository, status: contentStatus } = useContent();
    const {
        level:
            levelParameter
    } = useParams();

    const {
        t
    } = useI18n();

    const level =
        parseVocabularyLevel(
            levelParameter
        );

    const [
        packs,
        setPacks
    ] =
        useState<
            VocabPackIndex[]
        >(
            []
        );

    const [
        loading,
        setLoading
    ] =
        useState(
            true
        );

    const [loadError, setLoadError] = useState(false);

    const [
        retryCount,
        setRetryCount
    ] =
        useState(
            0
        );

    useEffect(
        () => {
            if (!level) {
                setPacks(
                    []
                );

                setLoading(
                    false
                );

                return;
            }

            if (contentStatus !== "ready" || !repository) {
                setLoading(contentStatus !== "error");
                setLoadError(contentStatus === "error");
                return;
            }
            const contentRepository = repository;

            let active =
                true;

            async function load(
                vocabularyLevel:
                    Level
            ): Promise<void> {
                setLoading(
                    true
                );
                setLoadError(false);
                try {
                    const loadedPacks = await loadPublishedVocabularyIndex(contentRepository, vocabularyLevel);
                    if (active) setPacks(loadedPacks);
                } catch {
                    if (active) setLoadError(true);
                } finally {
                    if (active) setLoading(false);
                }
            }

            void load(
                level
            );

            return () => {
                active =
                    false;
            };
        },
        [
            level,
            retryCount,
            repository,
            contentStatus
        ]
    );

    if (!level) {
        return (
            <Page>
                <BackButton
                    fallback="/vocabulary"
                >
                    ←
                    {" "}
                    {t(
                        "common.back"
                    )}
                </BackButton>

                <ErrorState
                    title={
                        t(
                            "error.notFound.title"
                        )
                    }
                    description={
                        t(
                            "vocab.packSoon"
                        )
                    }
                />
            </Page>
        );
    }

    if (loading) {
        return (
            <Page>
                <LoadingState
                    label={
                        t(
                            "common.loading"
                        )
                    }
                />
            </Page>
        );
    }

    if (loadError) {
        return <Page><ErrorState title={t("error.unavailable.title")} description={t("error.unavailable.body")} /></Page>;
    }

    return (
        <Page>
            <BackButton
                fallback="/vocabulary"
            >
                ←
                {" "}
                {t(
                    "common.back"
                )}
            </BackButton>

            <PageHeader
                icon="📖"
                eyebrow={
                    t(
                        "vocab.title"
                    )
                }
                title={
                    level
                }
                description={
                    t(
                        "vocab.chooseCategory"
                    )
                }
            />

            {packs.length > 0 ? (
                <VocabularyCatalog
                    level={
                        level
                    }
                    packs={
                        packs
                    }
                />
            ) : (
                <EmptyState
                    icon="📖"
                    title={
                        t(
                            "vocab.packSoon"
                        )
                    }
                    action={
                        <button
                            type="button"
                            className="
                                text-sm
                                font-bold
                                text-dino-700
                                hover:underline
                                hover:underline-offset-4
                            "
                            onClick={() => {
                                setRetryCount(
                                    current =>
                                        current + 1
                                );
                            }}
                        >
                            {t(
                                "common.retry"
                            )}
                        </button>
                    }
                />
            )}
        </Page>
    );
}

export {
    VocabularyLevelPage
};
